import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createMinerFamilyPricing,
  deleteMinerFamilyPricing,
  getCustomersMinerFamilyPricings,
  getMinerFamilyPricing,
  getMinerFamilyPricingHistory,
  getMinerFamilyPricings,
  updateMinerFamilyPricing,
} from '@/services/asicw/pricings.service'
import type {
  ICreateMinerFamilyPricingForm,
  IMinerFamilyPricingFilters,
  IMinerFamilyPricingHistoryFilters,
  IMinerModelFamilyPricing,
} from '@/types/asicw/pricings'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { ComputedRef, Reactive } from 'vue'

/**
 * Fixes numbers sent as strings over the wire
 */
const parsePricingNumbers = (pricing: any): IMinerModelFamilyPricing => ({
  ...pricing,
  wholesale_base_cost: parseFloat(pricing.wholesale_base_cost),
  modular_facility_type_adder: parseFloat(pricing.modular_facility_type_adder),
  traditional_facility_type_adder: parseFloat(pricing.traditional_facility_type_adder),
  immersion_cooling_type_adder: parseFloat(pricing.immersion_cooling_type_adder),
  water_cooling_type_adder: parseFloat(pricing.water_cooling_type_adder),
  air_cooling_type_adder: parseFloat(pricing.air_cooling_type_adder),
  third_party_firmware_adder: parseFloat(pricing.third_party_firmware_adder),
  overclocking_adder: parseFloat(pricing.overclocking_adder),
  repair_costs: {
    fan_basic_repair_cost: parseFloat(pricing.repair_costs?.fan_basic_repair_cost ?? 0),
    fan_replacement_cost: parseFloat(pricing.repair_costs?.fan_replacement_cost ?? 0),
    power_supply_basic_repair_cost: parseFloat(pricing.repair_costs?.power_supply_basic_repair_cost ?? 0),
    power_supply_advanced_repair_cost: parseFloat(pricing.repair_costs?.power_supply_advanced_repair_cost ?? 0),
    power_supply_replacement_cost: parseFloat(pricing.repair_costs?.power_supply_replacement_cost ?? 0),
    control_board_basic_repair_cost: parseFloat(pricing.repair_costs?.control_board_basic_repair_cost ?? 0),
    control_board_advanced_repair_cost: parseFloat(pricing.repair_costs?.control_board_advanced_repair_cost ?? 0),
    control_board_replacement_cost: parseFloat(pricing.repair_costs?.control_board_replacement_cost ?? 0),
    hashboard_basic_repair_cost: parseFloat(pricing.repair_costs?.hashboard_basic_repair_cost ?? 0),
    hashboard_advanced_repair_cost: parseFloat(pricing.repair_costs?.hashboard_advanced_repair_cost ?? 0),
    hashboard_replacement_cost: parseFloat(pricing.repair_costs?.hashboard_replacement_cost ?? 0),
  },
  additional_costs: {
    fan_labor_cost: parseFloat(pricing.additional_costs?.fan_labor_cost ?? 0),
    fan_shipping_cost: parseFloat(pricing.additional_costs?.fan_shipping_cost ?? 0),
    power_supply_labor_cost: parseFloat(pricing.additional_costs?.power_supply_labor_cost ?? 0),
    power_supply_shipping_cost: parseFloat(pricing.additional_costs?.power_supply_shipping_cost ?? 0),
    control_board_labor_cost: parseFloat(pricing.additional_costs?.control_board_labor_cost ?? 0),
    control_board_shipping_cost: parseFloat(pricing.additional_costs?.control_board_shipping_cost ?? 0),
    hashboard_labor_cost: parseFloat(pricing.additional_costs?.hashboard_labor_cost ?? 0),
    hashboard_shipping_cost: parseFloat(pricing.additional_costs?.hashboard_shipping_cost ?? 0),
  },
})

export const useModelFamilyPricings = () => {
  const queryClient = useQueryClient()

  const customersModelFamilyPricingsQuery = (filters: Reactive<IMinerFamilyPricingFilters>) =>
    useQuery({
      queryFn: () =>
        getCustomersMinerFamilyPricings(filters).then((pricingsPage) => ({
          ...pricingsPage,
          data: pricingsPage.data.map((pricing) => ({
            ...pricing,
            wholesale_base_cost: parseFloat(pricing.wholesale_base_cost as unknown as string),
          })),
        })),
      queryKey: ['customers-model-family-pricings', filters],
    })

  const modelFamilyPricingsQuery = (filters: Reactive<IMinerFamilyPricingFilters>) =>
    useQuery({
      queryFn: () =>
        getMinerFamilyPricings(filters).then((pricingsPage) => ({
          ...pricingsPage,
          data: pricingsPage.data.map(parsePricingNumbers),
        })),
      queryKey: ['model-family-pricings', filters],
    })

  const createFamilyPricingsMutation = useMutation({
    mutationFn: createMinerFamilyPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-family-pricings'] })
      NotifySuccess('Model Family Pricings created')
    },
    onError: NotifyErrorWrapper('Failed to create Model Family Pricings'),
  })

  const modelFamilyPricingDetailsQuery = (id: ComputedRef<number>) =>
    useQuery({
      queryFn: () => getMinerFamilyPricing(id.value),
      queryKey: ['model-family-pricings', id],
    })

  const updateModelFamilyPricingMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: ICreateMinerFamilyPricingForm }) =>
      updateMinerFamilyPricing(id, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-family-pricings'] })
      NotifySuccess('Model Family Pricings updated')
    },
    onError: NotifyErrorWrapper('Failed to update Model Family Pricings'),
  })

  const deleteMinerFamilyPricingMutation = useMutation({
    mutationFn: deleteMinerFamilyPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-family-pricings'] })
      NotifySuccess('Model Family Pricings deleted')
    },
    onError: NotifyErrorWrapper('Failed to delete Model Family Pricings'),
  })

  const minerFamilyPricingHistoryQuery = (
    id: ComputedRef<number>,
    filters: Reactive<IMinerFamilyPricingHistoryFilters>
  ) =>
    useQuery({
      queryFn: () => getMinerFamilyPricingHistory(id.value, filters),
      queryKey: ['model-family-pricings', id, 'history', filters],
    })

  return {
    customersModelFamilyPricingsQuery,
    modelFamilyPricingsQuery,
    createFamilyPricingsMutation,
    modelFamilyPricingDetailsQuery,
    updateModelFamilyPricingMutation,
    deleteMinerFamilyPricingMutation,
    minerFamilyPricingHistoryQuery,
  }
}
